import { useState, useEffect } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import ConfirmModal from "../employee/delete_modal";
import Dropdown from "../../components/modal/dropdown";
import Can from "../common/can";
import { toast } from "react-toastify";
import { getDepartments } from "../services/departmentservice";
import { getDesignations, deleteDesignation } from "../services/designationservice";
import { useAuth } from "../context/authcontext";
import Pagination from "../common/pagination";
import {
  DesignationStatusFilterOptions,
  getDesignationStatusLabel,
  getDesignationStatusClass,
} from "../../constants/constants";
import DesignationFormModal from "./adddesignation";

const PAGE_SIZE = 20;

const Designation = () => {
  const { hasAnyPermission, hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const canShowActions = isAdmin || hasAnyPermission(["designation.update", "designation.delete"]);

  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openModal, setOpenModal] = useState(false);
  const [editingDesignation, setEditingDesignation] = useState(null);

  /* ── FILTERS ── */
  const [filterDept, setFilterDept] = useState("All");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);

  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchDepartments = async () => {
    try {
      const res = await getDepartments({ page_size: 1000 }); // fetch all for dropdown
      const data = res.data || res;
      setDepartments(Array.isArray(data) ? data : data.results || []);
    } catch (error) {
      toast.error("Failed to load departments");
    }
  };

  const fetchDesignations = async () => {
    try {
      setLoading(true);
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (filterDept !== "All") {
        // filterDept now holds the department ID (see deptFilterOptions below)
        params.department = filterDept;
      }

      const res = await getDesignations(params);
      const data = res.data || res;
      const meta = res.meta || {};

      setDesignations(Array.isArray(data) ? data : data.results || []);
      setTotalCount(meta.count ?? data.count ?? (Array.isArray(data) ? data.length : 0));
    } catch (error) {
      toast.error("Failed to load designations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDesignations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, filterDept]);

  useEffect(() => {
    fetchDepartments();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, filterDept, statusFilter]);

  const deptOptions = departments.map((d) => ({ label: d.name, value: d.id }));
  // Options for the filter dropdown — "All" plus id-based department options,
  // so the selected value is always an id (or "All"), never a name.
  const deptFilterOptions = [{ label: "All", value: "All" }, ...deptOptions];
  const deptNames = departments.map((d) => d.name);

  const openAddModal = () => {
    setEditingDesignation(null);
    setOpenModal(true);
  };

  const openEditModal = (designation) => {
    setEditingDesignation(designation);
    setOpenModal(true);
  };

  const confirmDelete = async (password) => {
    try {
      await deleteDesignation(deleteTarget.id, password);
      await fetchDesignations();
      toast.success("Designation deleted successfully");
      setDeleteTarget(null);
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.detail || "Failed to delete designation: Incorrect password or permission denied";
      toast.error(msg);
      throw new Error(msg);
    }
  };

  /* ── FILTER ──
     Backend currently applies the department filter server-side, but
     status is filtered client-side here the same way Department does it,
     in case the designation list endpoint returns inactive rows too. */
  const filtered = designations.filter((d) => {
    if (statusFilter === "all") return true;
    return d.is_active === statusFilter;
  });
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const paginated = filtered;

  return (
    <div className="container">
      {/* ───────────────── HEADER ───────────────── */}
      <div className="page-header">
        <h2>Designations</h2>
        <Can perm="designation.create">
          <button className="primary-btn" onClick={openAddModal} disabled={deptNames.length === 0}>
            Add New Designation
          </button>
        </Can>
      </div>

      {/* ───────────────── FILTERS ───────────────── */}
      <div className="filters-card single">
        <input
          className="search-input"
          placeholder="🔍 Search designations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Dropdown
          options={deptFilterOptions}
          value={filterDept}
          placeholder="Filter by Department"
          onChange={setFilterDept}
        />
        <Dropdown
          options={DesignationStatusFilterOptions}
          value={statusFilter}
          placeholder="Status"
          onChange={setStatusFilter}
        />
      </div>

      {loading ? (
        <div className="empty-state">
          <p>Loading Designations...</p>
        </div>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Department</th>
                <th>Designation</th>
                <th>Status</th>
                {canShowActions && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={canShowActions ? "4" : "3"}>
                    <div className="empty-state">
                      <img src="/images/nodesignation.svg" alt="No designations" className="empty-img" />
                      <p>No Designations Found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((desig) => (
                  <tr key={desig.id}>
                    <td>
                      <span className="desig-dept-tag">{desig.department_name}</span>
                    </td>

                    <td>
                      <strong>{desig.name}</strong>
                    </td>

                    <td>
                      <span className={`emp-status ${getDesignationStatusClass(desig.is_active)}`}>
                        {getDesignationStatusLabel(desig.is_active)}
                      </span>
                    </td>

                    {canShowActions && (
                      <td className="action-icons">
                        <Can perm="designation.update">
                          <button className="icon-btn edit" onClick={() => openEditModal(desig)}>
                            <FaEdit />
                          </button>
                        </Can>
                        <Can perm="designation.delete">
                          <button className="icon-btn delete" onClick={() => setDeleteTarget(desig)}>
                            <FaTrash />
                          </button>
                        </Can>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <Pagination page={page} totalPages={totalPages} setPage={setPage} />
        </>
      )}

      {openModal && (
        <DesignationFormModal
          editingDesignation={editingDesignation}
          deptOptions={deptOptions}
          existingDesignations={designations}
          onClose={() => setOpenModal(false)}
          onSaved={() => { fetchDesignations(); setOpenModal(false); }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Designation"
          message={`Are you sure you want to delete "${deleteTarget.name}"?`}
          confirmText="Delete"
          type="danger"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default Designation;